import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import PartnersApi from 'libs/api/website/partners';
import AuthService from 'services/Auth';

const usePartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [shoppingCenters, setShoppingCenters] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const navigate = useNavigate();
  const paramsRef = useRef({});

  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchPartners = useCallback(async (params = {}) => {
    if (!checkAuth()) return;
    setLoading(true);
    try {
      const response = await PartnersApi.list(params);
      if (response.data.success) {
        const rows = response.data.partners || [];
        setPartners(rows);
        setTotal(response.data.pagination?.total_count || rows.length || 0);
      } else {
        setPartners([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load partners');
      }
    } finally {
      setLoading(false);
    }
  }, [checkAuth, navigate]);

  const fetchFilters = useCallback(async () => {
    if (!checkAuth()) return;
    setLoadingCompanies(true);
    try {
      const response = await PartnersApi.getFilters();
      const shoppingCentersRaw = response.data.shopping_centers || [];
      setShoppingCenters(shoppingCentersRaw);
    } catch (error) {
      console.error('Error fetching filters:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error('Failed to load filters');
      }
    } finally {
      setLoadingCompanies(false);
    }
  }, [checkAuth, navigate]);

  const fetchPartner = useCallback(async (id) => {
    if (!checkAuth()) return null;
    try {
      const response = await PartnersApi.show(id);
      if (response.data.success) {
        return response.data.partner;
      }
      return null;
    } catch (error) {
      console.error('Error fetching partner:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load partner');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const createPartner = useCallback(async (data) => {
    if (!checkAuth()) return null;
    try {
      const response = await PartnersApi.create(data);
      if (response.data.success) {
        return response.data.success;
      }
      return null;
    } catch (error) {
      console.error('Error creating partner:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to create partner');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const updatePartner = useCallback(async (id, data) => {
    if (!checkAuth()) return null;
    try {
      const response = await PartnersApi.update(id, data);
      if (response.data.success) {
        return response.data.partner;
      }
      return null;
    } catch (error) {
      console.error('Error updating partner:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to update partner');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const deletePartner = useCallback(async (id) => {
    if (!checkAuth()) return false;
    try {
      await PartnersApi.destroy(id);
      message.success('Partner deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting partner:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to delete partner');
      }
      return false;
    }
  }, [checkAuth, navigate]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    const params = {
      page: currentPage,
      per: pageSize,
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    });
    paramsRef.current = params;
    fetchPartners(params);
  }, [currentPage, pageSize, filters, fetchPartners]);

  const updateFilters = useCallback((newFilters) => {
    if (Object.keys(newFilters).length === 0) {
      setFilters({});
    } else {
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  return {
    partners,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingCompanies,
    updateFilters,
    handlePageChange,
    deletePartner,
    createPartner,
    updatePartner,
    fetchPartner,
    refetch: () => fetchPartners(paramsRef.current),
  };
};

export default usePartners;


